/**
 * useScreenShareToggle (web) — partager son écran dans la salle, depuis un navigateur.
 *
 * Le geste du bouton de la barre de l'ancien live (`ControlBarM.web`, `TrackToggleM` sur
 * `Track.Source.ScreenShare`) et de `common/hooks/livekit/useScreenShare.web.js`, à l'identique :
 * le même interrupteur de LiveKit (`useTrackToggle`), les mêmes options de capture, lues au même
 * endroit (`SCREEN_SHARE_CAPTURE_OPTIONS`, `screenShareToggle.ts`), la même règle d'affichage (le navigateur sait partager,
 * le participant a le droit de publier). Le tuteur reçoit une piste `screen_share` : il la voit
 * comme aujourd'hui, le protocole de la salle ne change pas.
 *
 * L'élève qui ferme le sélecteur du navigateur sans choisir laisse un fil d'Ariane, pas une erreur ;
 * un autre échec part dans Bugsnag.
 *
 * Le bouton qui paraît ou disparaît laisse aussi son fil d'Ariane, avec sa raison (`screenShareGate`) :
 * il a disparu une fois de la barre au grand écran au weekly du 20/10, sans qu'on sache pourquoi ; la
 * prochaine fois, le fil le dira (le navigateur qui ne sait pas partager, ou le droit de publier pas
 * encore reçu de la salle).
 */
import { supportsScreenSharing } from "@livekit/components-core"
import { useLocalParticipantPermissions, useTrackToggle } from "@livekit/components-react"
import { Track } from "livekit-client"
import { useCallback, useEffect, useRef } from "react"

import { type EngineTelemetry } from "../../shared/telemetry"
import { SCREEN_SHARE_CAPTURE_OPTIONS, screenShareGate, type ScreenShareToggle } from "./screenShareToggle"

/** Le sélecteur du navigateur fermé sans choix : un geste de l'élève, pas une panne. */
const isPickerDismissed = (error: Error) => error?.name === "NotAllowedError" || error?.name === "AbortError"

export function useScreenShareToggle(telemetry: EngineTelemetry): ScreenShareToggle | null {
    const onDeviceError = useCallback(
        (error: Error) => {
            if (isPickerDismissed(error)) telemetry.transition("off", "off", "screen_share_dismissed")
            else telemetry.error("screen_share", error)
        },
        [telemetry]
    )
    const { toggle, enabled, pending } = useTrackToggle({
        source: Track.Source.ScreenShare,
        captureOptions: SCREEN_SHARE_CAPTURE_OPTIONS,
        onDeviceError,
    })
    const permissions = useLocalParticipantPermissions()
    // Sans argument : `toggle(x)` forcerait l'état à `x`.
    const onToggle = useCallback(() => {
        void toggle()
    }, [toggle])

    const gate = screenShareGate({ supported: supportsScreenSharing(), canPublish: permissions?.canPublish })
    // Le bouton paraît ou disparaît : un fil d'Ariane, avec sa raison.
    const gateRef = useRef<string | null>(null)
    useEffect(() => {
        if (gateRef.current === gate) return
        telemetry.transition(gateRef.current ?? "mount", gate, "screen_share_gate")
        gateRef.current = gate
    }, [gate, telemetry])

    if (gate !== "shown") return null
    return { on: enabled, pending, onToggle }
}
